import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom"; 
import "./DICOMUploader.css";

export default function DICOMUploader() {
  const [files, setFiles] = useState([]);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [globalProgress, setGlobalProgress] = useState(0); 
  const [isFolderUpload, setIsFolderUpload] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [currentFileName, setCurrentFileName] = useState("");
  const [actualUploadedCount, setActualUploadedCount] = useState(0); // NOUVEAU: Compte réel depuis l'API
  const inputFileRef = useRef(null);
  const inputFolderRef = useRef(null);
  const navigate = useNavigate();

  function makeId() {
    return Math.random().toString(36).slice(2, 9);
  }

  function onFilesAdded(list, fromFolder = false) {
    const dicomFiles = Array.from(list);

    if (dicomFiles.length === 0) {
      alert("⚠️ Aucun fichier DICOM trouvé !");
      return;
    }

    const newItems = dicomFiles.map((f) => ({
      file: f,
      id: makeId(),
      progress: 0,
      status: "idle",
      error: null,
    }));

    setFiles(newItems);
    setUploadedCount(0);
    setActualUploadedCount(0); // NOUVEAU: Reset du compte réel
    setTotalCount(newItems.length);
    setGlobalProgress(0);
    setIsFolderUpload(fromFolder);
    setCurrentFileName("");
  }

  async function uploadItem(item, index) {
    const token = localStorage.getItem("token");
    if (!token) {
      item.status = "error";
      item.error = "Token non trouvé";
      setFiles((s) => [...s]);
      return;
    }

    const form = new FormData();
    form.append("files", item.file);

    try {
      item.status = "uploading";
      setCurrentFileName(item.file.name);
      setFiles((s) => [...s]);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", process.env.REACT_APP_EUREKA_PACS_URL + "/api/upload", true);

        xhr.setRequestHeader("Authorization", `Bearer ${token}`);

        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            const percent = Math.round((ev.loaded / ev.total) * 100);
            item.progress = percent;
            setFiles((s) => [...s]);

            if (isFolderUpload) {
              const totalProgress =
                (files.reduce((acc, f, idx) => acc + (idx === index ? percent : f.progress), 0)) /
                totalCount;
              setGlobalProgress(Math.round(totalProgress));
            }
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // NOUVEAU: Parser la réponse de l'API
            try {
              const responseText = xhr.responseText;
              
              // Extraire le nombre de fichiers depuis la réponse
              // Ex: "0 file(s) uploaded successfully." ou "1 file(s) uploaded successfully."
              const match = responseText.match(/(\d+)\s+file\(s\)\s+uploaded\s+successfully/i);
              const uploadedFromApi = match ? parseInt(match[1], 10) : 0;

              if (uploadedFromApi > 0) {
                item.status = "success";
                item.progress = 100;
                setUploadedCount((c) => c + 1);
                setActualUploadedCount((c) => c + uploadedFromApi); // NOUVEAU: Compte réel
                resolve(responseText);
              } else {
                // L'API dit 0 fichier uploadé
                item.status = "error";
                item.error = "Fichier non accepté par le serveur";
                reject(new Error("Fichier non accepté"));
              }
            } catch (parseErr) {
              // Si on ne peut pas parser, on considère comme erreur
              item.status = "error";
              item.error = "Réponse serveur invalide";
              reject(parseErr);
            }
          } else {
            item.status = "error";
            item.error = `HTTP ${xhr.status}`;
            reject(new Error(`HTTP ${xhr.status}`));
          }
          setFiles((s) => [...s]);
        };

        xhr.onerror = () => {
          item.status = "error";
          item.error = "Erreur réseau";
          setFiles((s) => [...s]);
          reject(new Error("Erreur réseau"));
        };

        xhr.send(form);
      });
    } catch (err) {
      item.status = "error";
      item.error = err.message || "Échec du téléversement";
      setFiles((s) => [...s]);
    }
  }

  async function uploadAll() {
    setActualUploadedCount(0); // NOUVEAU: Reset avant upload
    for (let i = 0; i < files.length; i++) {
      const item = files[i];
      if (item.status === "idle" || item.status === "error") {
        await uploadItem(item, i);
      }
    }
    setShowResultModal(true);
  }

  function removeFile(id) {
    setFiles((s) => s.filter((f) => f.id !== id));
  }

  return (
    <div className="uploader-container">
      <div className="uploader-header">
        <h2>Téléversement de fichiers DICOM</h2>
        <div className="uploader-buttons">
          <button className="btn btn-home" onClick={() => navigate("/accueil")}>
            Accueil
          </button>
          <button className="btn browse" onClick={() => setShowChoiceModal(true)}>
            Parcourir
          </button>
          <button className="btn upload" onClick={uploadAll}>
            Téléverser
          </button>
        </div>
      </div>

      {/* Zone de drop */}
      <div className="uploader-dropzone"
        onDrop={(e) => { e.preventDefault(); onFilesAdded(e.dataTransfer.files); }}
        onDragOver={(e) => e.preventDefault()}
      >
        <p>Glissez-déposez vos fichiers ici ou cliquez sur <strong>Parcourir</strong>.</p>
        <input
          ref={inputFileRef}
          type="file"
          multiple
          className="hidden-input"
          onChange={(e) => onFilesAdded(e.target.files, false)}
        />
        <input
          ref={inputFolderRef}
          type="file"
          webkitdirectory="true"
          directory=""
          className="hidden-input"
          onChange={(e) => onFilesAdded(e.target.files, true)}
        />
      </div>

      {/* MODE DOSSIER : Barre unique sans liste */}
      {isFolderUpload && totalCount > 0 && (
        <div className="folder-upload-mode">
          <div className="global-progress-card">
            <div className="progress-header">
              <h3>📦 Téléversement en cours</h3>
              <span className="progress-count">{uploadedCount}/{totalCount} fichiers traités</span>
            </div>
            
            {currentFileName && (
              <div className="current-file">
                <span className="current-file-label">Fichier en cours :</span>
                <span className="current-file-name">{currentFileName}</span>
              </div>
            )}
            
            <div className="progress-bar-wrapper">
              <div className="progress-bar global" style={{ width: `${globalProgress}%` }}>
                <span className="progress-text">{globalProgress}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODE FICHIERS : Liste classique avec barres individuelles */}
      {!isFolderUpload && files.length > 0 && (
        <div className="uploader-file-list">
          {files.map((item) => (
            <div key={item.id} className="uploader-file">
              <div className="file-info">
                <span>{item.file.name}</span>
                <span>{(item.file.size / 1024 / 1024).toFixed(2)} Mo</span>
              </div>
              <div className="file-progress">
                <div className={`progress-bar ${item.status}`} style={{ width: `${item.progress}%` }}></div>
              </div>
              <div className="file-actions">
                {item.status === "error" && (
                  <button className="btn retry" onClick={() => uploadItem(item)}>
                    Réessayer
                  </button>
                )}
                <button className="btn remove" onClick={() => removeFile(item.id)}>
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL RESULTAT - MODIFIÉ */}
      {showResultModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            {actualUploadedCount > 0 ? (
              <>
                <h3>✅ Téléversement terminé</h3>
                <p><strong>{actualUploadedCount}</strong> fichier(s) DICOM téléversé(s) avec succès.</p>
                {uploadedCount > actualUploadedCount && (
                  <p className="warning-text">⚠️ {uploadedCount - actualUploadedCount} fichier(s) refusé(s) par le serveur (format invalide ou déjà existant).</p>
                )}
              </>
            ) : (
              <>
                <h3>❌ Échec du téléversement</h3>
                <p>Aucun fichier DICOM n'a été téléversé avec succès.</p>
                <p className="warning-text">Vérifiez que vos fichiers sont au format DICOM valide.</p>
              </>
            )}
            <div className="modal-buttons">
              <button className="btn option" onClick={() => { setShowResultModal(false); navigate("/accueil"); }}>
                Retour à l'accueil
              </button>
              <button className="btn cancel" onClick={() => setShowResultModal(false)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CHOIX */}
      {showChoiceModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>📂 Choisir une option</h3>
            <p>Voulez-vous téléverser des fichiers DICOM ou un dossier complet ?</p>
            <div className="modal-buttons">
              <button className="btn option" onClick={() => { setShowChoiceModal(false); inputFileRef.current?.click(); }}>
                Sélectionner des fichiers
              </button>
              <button className="btn option" onClick={() => { setShowChoiceModal(false); inputFolderRef.current?.click(); }}>
                Sélectionner un dossier
              </button>
            </div>
            <button className="btn cancel" onClick={() => setShowChoiceModal(false)}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}